import { ICardPaymentBrickPayer, ICardPaymentFormData } from '@mercadopago/sdk-react/bricks/cardPayment/type';
import axios from 'axios';
import { AUTHSTORAGE_KEY } from 'contexts/useAuth';
import { FormCardCreditCard } from 'layouts/charge/PagSeguroCard';
import { ChargePublic } from 'layouts/charge/types/ChargeTypes';
import { Carnet, CarnetAccountFeatures, CarnetItem, CreateCarnetRequest } from 'types/Carnet';
import { Charge, InstantQrCode } from 'types/Charge';
import { ConsultCep } from 'types/ConsultCep';
import { ConsultCnpj } from 'types/ConsultCnpj';
import { Movement } from 'types/Movement';
import { DashboardResponse } from 'types/dashboard';
import type { AccountLateCharges } from 'utils/lateCharges';
import { CreateHolderForm } from 'views/access/insurances/components/CreateHolder';
import { ConsolidatedReport } from 'views/access/reports/consolidated/types';
import { Withdrawal } from 'views/access/withdrawals';
import { BilletResponse, ConsultPixKey, ShowQrCode } from 'views/access/withdrawals/types/QRcode/qrcode.type';
import { RecurrenceResponse } from './types/Recurrence.entity';
import { RecurrencyClientPayments } from './types/RecurrencyPayments.entity';
import { CardList } from './types/cards.type';
import { CustomerInfo } from './types/customer';
import { Dispute } from './types/dispute';
import { InstallmentsConfig } from './types/installmensts.config';
import { Onboarding } from './types/onboarding.type';
import { RecurrencyLink, RecurrencyLinkItemGet } from './types/recurrency-link';

export interface RechargeFormCardCreditCard {
  cardNumber: string;
  cardExpiration: string;
  cardCvv: string;
  cardHolderName: string;
  cardHolderCpf: string;
  installments: number;
  amount: number;
  pagSeguro3dsId?: string;
  method: 'CREDIT_CARD' | 'DEBIT_CARD';
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Set authorization header if token exists
const token = localStorage.getItem('Authorization');
if (token) {
  api.defaults.headers.Authorization = 'Bearer ' + token;
}

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const baseUrl = error.config.url;
    const routesIgnore = ['/customer', '/auth'];
    if (error.response?.status === 401 && !routesIgnore.some((route) => baseUrl.includes(route))) {
      localStorage.removeItem(AUTHSTORAGE_KEY);
      dispatchEvent(new CustomEvent('auth:logout'));
    }
    return Promise.reject(error);
  },
);

// Auto-refresh contract status when the backend returns CONTRACT_REQUIRED.
// The ContractsContext will react via react-query refetch on focus, but
// dispatching a custom event lets the modal open immediately on the same tick.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const code = err?.response?.data?.code;
    if (code === 'CONTRACT_REQUIRED') {
      // Notify ContractsContext to re-poll status (it has a window-focus listener,
      // but a custom event is more deterministic)
      try {
        window.dispatchEvent(new CustomEvent('contracts:re-check'));
      } catch {
        // ignore
      }
    }
    return Promise.reject(err);
  },
);

interface Orders {
  id: number;
  customerName: string;
  providerName: string;
  status: string;
  serviceType: string;
  amount: number;
  on: Date;
}

export const validateUserCode = async (code: string) => {
  return api.post<{ id: string }>('customers/validateCode', { code }).then((res) => res.data);
};

export const loginCustomerService = async (
  email: string,
  password: string,
  captchaKey: string | undefined,
  token: string | undefined,
  turnstileToken?: string,
) => {
  return api
    .post('/auth/customer', { email, password, captchaKey, turnstileToken, twoFa: token })
    .then((res) => res.data);
};

export const deleteWithdrawal = (id: string) => {
  return api.delete(`/withdrawals/${id}`);
};

export const updateCustomerService = () => {
  return api.get('/auth/update').then((res) => res.data);
};

export const getAccountsAccess = async () => {
  return api
    .get<{ customerAccess: { id: string; name: string; email: string; accountNumber: string } }[]>('/customers/access')
    .then((res) => res.data);
};

export const changeUserAccount = async (customerIdToAccess: string) => {
  return api.post('/auth/customer/change-user', { customerIdToAccess }).then((res) => res.data);
};

export const getAllowCardRecharge = async () => {
  return api.get<boolean>('/charges/allow-card-recharge').then((res) => res.data);
};

export const findClientById = async (id: string) => {
  return api
    .get<{
      id: string;
      name: string;
      document: string;
      email: string;
      phone: string;
    }>(`/clients/${id}`)
    .then((res) => res.data);
};

export const createClient = async (data: { email: string; phone: string; name: string; document: string }) => {
  return api.post(`clients`, data).then((res) => res.data);
};

export const editClient = async (
  id: string,
  data: {
    email: string;
    phone: string;
    name: string;
    document: string;
  },
) => {
  return api.put(`clients/${id}`, data).then((res) => res.data);
};

export const deleteClient = async (id: string) => {
  return api.delete(`clients/${id}`).then((res) => res.data);
};

export const realizarLevantamentoApi = async (data: { recurrencyId: string; amount: number }) => {
  return api.post(`recurrences/${data.recurrencyId}/withdraw`, data).then((res) => res.data);
};

export const createRecurrency = async (data: { amount: string; description: string }) => {
  return api.post(`recurrences`, data).then((res) => res.data);
};

export const deleteRecurrency = async (id: string) => {
  return api.delete(`recurrences/${id}`).then((res) => res.data);
};

export const findClientPayments = async (id: string) => {
  return api.get<RecurrencyClientPayments>(`recurrences/payments/${id}`).then((res) => res.data);
};

export interface RecurrenceClientsSummary {
  activeClients: number;
  activePlans: number;
  overdueClients: number;
  upcomingClients: number;
  plansWithoutWallet: number;
  pendingAmount: number;
  paidAmount: number;
}

export const findRecurrenceClientsSummary = async () => {
  return api.get<RecurrenceClientsSummary>(`recurrences/clients/summary`).then((res) => res.data);
};

export const removeClientFromRecurrence = async (id: string) => {
  return api.delete(`recurrences/clients/${id}`).then((res) => res.data);
};

export const updateRecurrenceClient = async (
  id: string,
  data: { initDate?: string; endDate?: string; nextDate?: string; lastDate?: string; period?: string },
) => {
  return api.put(`recurrences/clients/${id}`, data).then((res) => res.data);
};

export const createNewRecurrencePaymentRequest = async ({
  id,
  withoutCharge,
}: {
  id: string;
  withoutCharge: boolean;
}) => {
  return api.post(`recurrences/payments/${id}/charge`, { withoutCharge }).then((res) => res.data);
};
export const sendMailRecurrencePaymentRequest = async (id: string) => {
  return api.post(`recurrences/generated/${id}/mail`).then((res) => res.data);
};
export const doneRecurrencePayment = async (id: string) => {
  return api.put(`recurrences/generated/${id}/done`).then((res) => res.data);
};

export const cancelRecurrencePayment = async (id: string) => {
  return api.delete(`recurrences/generated/${id}`).then((res) => res.data);
};

export const newChargeForGeneratedRecurrence = async (id: string) => {
  return api.put(`recurrences/generated/${id}`).then((res) => res.data);
};

export const updateRecurrencePaymentDueDate = async ({
  id,
  data,
}: {
  id: string;
  data: { dueDate: string };
}) => {
  return api.put(`recurrences/generated/${id}/due-date`, data).then((res) => res.data);
};

export const linkWalletToRecurrencyApi = async ({ id, walletId }: { id: string; walletId: string }) => {
  return api.put(`recurrences/${id}/wallet/${walletId}`).then((res) => res.data);
};

export const findAllWallets = async (filter: { limit: number; page: number; filter: string }) => {
  return api
    .get(`wallets`, {
      params: filter,
    })
    .then((res) => res.data);
};

export const addClientToRecurrence = async (
  recurrenceId: string,
  data: {
    clientId: string;
    initDate: string;
    period: string;
    endDate: string;
    generateAllUpfront?: boolean;
  },
) => {
  return api.post(`recurrences/${recurrenceId}/add`, data).then((res) => res.data);
};

export const updateUser = (
  id: string,
  data: {
    email: string;
    password: string;
    name: string;
    companyId: string;
  },
) => {
  return api.put(`users/${id}`, data).then((res) => res.data);
};

export const deleteCustomerTaxes = (customerId: string, taxId: string) => {
  return api.delete(`customers/${customerId}/taxes/${taxId}`).then((res) => res.data);
};

export const getMovement = (id: string) => {
  return api.get<Movement>(`movements/${id}`).then((res) => res.data);
};

export const getRecurrence = async (id: string) => {
  return api.get<RecurrenceResponse>(`recurrences/${id}`).then((res) => res.data);
};

export const deleteTax = (id: string) => {
  return api.delete(`taxes/${id}`).then((res) => res.data);
};
export const ativeUserService = (id: string, ative: boolean) => {
  return api.put(`users/${id}/ative`, { ative }).then((res) => res.data);
};

export interface Customer {
  id: string;
  name: string;
  document_number: string;
  email: string;
  status: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerListResponse {
  pages: number;
  registers: Customer[];
}

export const findAllParticipants = ({
  limit = 20,
  filter = '',
  page = 0,
}: {
  limit?: number;
  page?: number;
  filter?: string;
}) => {
  return api
    .get<CustomerListResponse>('withdrawals/participants', {
      params: {
        limit,
        page,
        filter,
      },
    })
    .then((res) => res.data);
};

export const createWithdrawal = (authorizationId: string, data: any) => {
  return api
    .post<any>('withdrawals', data, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};

export const createWithdrawalQRCode = async (
  authorizationId: string,
  data: {
    amount: number;
    description: string;
    qrcode: string;
    receiverName?: string;
  },
) => {
  return api
    .post<any>('withdrawals/qrcode', data, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};
export const createWithdrawalAntecipate = (data: any) => {
  return api.post<any>('withdrawals/antecipate', data).then((res) => res.data);
};

export const createTransfer = (authorizationId: string, data: any) => {
  return api
    .post<any>('withdrawals/transfer', data, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};

export const createCharge = async (data: {
  amount: number;
  description: string;
  document?: string;
  name?: string;
  dueDate?: string;
  feeType: 'FROM_PAYER' | 'FROM_CUSTOMER';
  intermediary: boolean;
  releaseInDays: number;
  maxInstallments: number | null;
  itens?: { description: string; amount: number }[];
  paymentMethod?: 'PIX' | 'BILLET';
  latePenaltyPercent?: number;
  latePenaltyAmount?: number;
  lateMonthlyInterestPercent?: number;
}) => {
  return api.post<any>('charges', data).then((res) => res.data);
};

export const validateQRCode = async (qrcode: string) => {
  return api.post<ShowQrCode>('withdrawals/validate', { qrcode }).then((res) => res.data);
};
export const validateBillet = async (billet: string) => {
  return api.post<BilletResponse>('withdrawals/validate-billet', { billet }).then((res) => res.data);
};

export const createBilletPayment = async (data: { billetId: string; amount: number; twoFaId: string }) => {
  return api
    .post<BilletResponse>(
      'withdrawals/create-billet-payment',
      {
        billetId: data.billetId,
        amount: data.amount,
      },
      {
        headers: {
          'x-security-code': data.twoFaId,
        },
      },
    )
    .then((res) => res.data);
};

export const validatePixKey = async (key: string) => {
  return api.post<ConsultPixKey>('withdrawals/validate-pix', { key }).then((res) => res.data);
};

export const transferAmountAnotherCustomer = (customerId: string, data: any) => {
  return api.post<any>(`customers/${customerId}/transfer`, data).then((res) => res.data);
};

export const findAllClientsFilter = ({ filter, limit }: { limit: number; filter: string }) => {
  return api
    .get('customers/simple', {
      params: {
        limit,
        filter,
      },
    })
    .then((res) => res.data);
};

export const findCnpj = (cnpj: string) => {
  return api.get<ConsultCnpj>(`customers/cnpj/${cnpj}`).then((res) => res.data);
};

export const findCep = (cep: string) => {
  return api.get<ConsultCep>(`ceps/${cep}`).then((res) => res.data);
};

export const changePasswordCustomer = (customerId: string, password: string, currentPassword: string) => {
  return api
    .put(`customers/${customerId}/changePassword`, {
      password,
      currentPassword,
    })
    .then((res) => res.data);
};

export const findAllTerminals = (filters: {
  limit: number;
  page: number;
  filter: string;
  orderBy: { id: string; desc: boolean }[];
}) => {
  return api
    .get<{ total: number; registers: any[] }>('maquinetas', {
      params: filters,
    })
    .then((res) => res.data);
};

export const findAllWithdrawers = (filter: {
  limit: number;
  page: number;
  filter: string;
  query?: Record<string, any>;
  initialDate?: string;
  finalDate?: string;
}) => {
  // POST /withdrawals/list carrega o objeto completo (incl. query.status) no body.
  return api
    .post<{ pages: number; registers: Withdrawal[] }>('withdrawals/list', filter)
    .then((res) => res.data);
};

export const updateWithdrawel = (
  id: string,
  data: {
    amount: number;
  },
) => {
  return api.put<Withdrawal>(`withdrawals/${id}`, data).then((res) => res.data);
};

export const findWithdrawel = (id: string) => {
  return api.get<Withdrawal>(`withdrawals/${id}`).then((res) => res.data);
};

export const getPendingWithdrawalsForApproval = () => {
  return api.get<Withdrawal[]>('withdrawals/pending-approvals').then((res) => res.data);
};

export const approveWithdrawal = (
  id: string,
  data: {
    status: 'APPROVED' | 'REJECTED';
    observation?: string;
  },
) => {
  return api
    .post<{
      message: string;
      status: string;
      approvals?: number;
      required?: number;
    }>(`withdrawals/${id}/approve`, data)
    .then((res) => res.data);
};

export const findAllMovements = (
  filter: {
    limit: number;
    customerId: string;
    page: number;
    maquinetaId?: string;
    filter: string;
  },
  exportPdf = false,
  exportOptions?: { deliverByEmail?: boolean; forceSync?: boolean },
) => {
  return api
    .post<Orders>(
      'movements/all',
      {
        ...filter,
        exportPdf,
        ...(exportPdf && {
          asyncExportSupported: true,
          ...(exportOptions?.deliverByEmail && { deliverByEmail: true }),
          ...(exportOptions?.forceSync && { forceSync: true }),
        }),
      },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export interface ChargebackDTO {
  id: string;
  movementId: string;
  status: 'OPEN' | 'IN_DISPUTE' | 'ACCEPTED' | 'REVERSED' | 'CANCELED';
  source: 'MANUAL' | 'PAGSEGURO_ONLINE' | 'PAGSEGURO_TERMINAL';
  reason: string;
  blockedAmount: string; // Decimal serialized as string — Number() before formatting
  chargebackDate: string | null;
  createdAt: string;
  movement: {
    id: string;
    transactionCode: string;
    amount: string; // Decimal as string
    orderAt: string;
    type: string;
    maquinetaId: string | null;
    status: string;
  };
  customer: { name: string };
}

export const findMyChargebacks = (filter: {
  page: number;
  limit: number;
  query?: { status?: string };
  initialDate?: string;
  finalDate?: string;
  [key: string]: unknown;
}) => {
  const { query, ...rest } = filter;
  const status = query?.status || undefined; // '' (placeholder) => omit => backend default = active
  return api
    .post<{ registers: ChargebackDTO[]; pages: number }>('chargebacks/mine', {
      ...rest,
      query: status ? { status } : undefined,
    })
    .then((res) => res.data);
};

export const findAllClients = (
  filter: {
    limit: number;
    page: number;
    maquinetaId?: string;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post(
      'clients/all',
      {
        ...filter,
        exportPdf,
      },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export const findAllRecurrences = async (
  filter: {
    limit: number;
    page: number;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post(
      'recurrences/all',
      {
        ...filter,
        exportPdf,
      },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

export const findAllRecurrencesClients = async (filter: {
  limit: number;
  customerId: string;
  page: number;
  maquinetaId?: string;
  filter: string;
}) => {
  return api
    .get('recurrences/clients', {
      params: {
        ...filter,
      },
    })
    .then((res) => res.data);
};

export const findAllBalances = async (
  params: {
    limit: number;
    customerId?: string;
    page: number;
    filter: string;
    exportCsv?: boolean;
  },
  exportPdf: boolean,
  exportOptions?: { deliverByEmail?: boolean; forceSync?: boolean },
) => {
  return api
    .get<{
      pages: number;
      registers: any[];
    }>('balances', {
      params: {
        ...params,
        ...(exportPdf && !params.exportCsv ? { exportPdf: true } : {}),
        ...(exportPdf && {
          asyncExportSupported: true,
          ...(exportOptions?.deliverByEmail && { deliverByEmail: true }),
          ...(exportOptions?.forceSync && { forceSync: true }),
        }),
      },
      responseType: exportPdf ? 'blob' : 'json',
    })
    .then((res) => res.data);
};

export const findAllBalancesPending = (params: {
  limit: number;
  customerId?: string;
  page: number;
  filter: string;
}) => {
  return api
    .get<{
      pages: number;
      registers: any[];
    }>('balances-pending', {
      params,
    })
    .then((res) => res.data);
};

export const getBalancesTotalsByOrigin = async (params: {
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{ key: string; total: number }[]>('balances/totals-by-origin', { params })
    .then((res) => res.data);
};

export const findReceivedFees = async (
  params: { limit: number; customerId?: string; page: number; filter?: string; initialDate?: string; finalDate?: string; originCustomerId?: string },
  exportPdf?: boolean,
) => {
  return api
    .get<{ pages: number; registers: any[] }>('balances/received-fees', {
      params,
      responseType: exportPdf ? 'blob' : 'json',
    })
    .then((res) => res.data);
};

export const getBalancesFeesSummary = async (params: {
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{ total: number; byType: { type: string; total: number; count: number }[] }>(
      'balances/fees-summary',
      { params },
    )
    .then((res) => res.data);
};

export const getBalancesFeesBySeller = async (params: {
  limit: number;
  page: number;
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{
      pages: number;
      registers: {
        originCustomerId: string;
        originName: string | null;
        total: number;
        count: number;
      }[];
    }>('balances/fees-by-seller', { params })
    .then((res) => res.data);
};

export const findReceivedSplits = async (
  params: { limit: number; customerId?: string; page: number; filter?: string; initialDate?: string; finalDate?: string; originCustomerId?: string },
  exportPdf?: boolean,
) => {
  return api
    .get<{ pages: number; registers: any[] }>('balances/received-splits', {
      params,
      responseType: exportPdf ? 'blob' : 'json',
    })
    .then((res) => res.data);
};

export const getBalancesSplitsSummary = async (params: {
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{ total: number; byType: { type: string; total: number; count: number }[] }>(
      'balances/splits-summary',
      { params },
    )
    .then((res) => res.data);
};

export const getBalancesSplitsByOrigin = async (params: {
  limit: number;
  page: number;
  customerId?: string;
  initialDate?: string;
  finalDate?: string;
}) => {
  return api
    .get<{
      pages: number;
      registers: {
        originCustomerId: string;
        originName: string | null;
        total: number;
        count: number;
      }[];
    }>('balances/splits-by-origin', { params })
    .then((res) => res.data);
};

export const dashboardCustomer = async (filter: any) => {
  return api.get<DashboardResponse>(`dashboard/customer`, { params: filter }).then((res) => res.data);
};

export const deleteCustomerAccount = (id: string) => {
  return api.delete(`accounts/${id}`).then((res) => res.data);
};

export const deleteCustomer = async (authorizationId: string) => {
  return api
    .delete(`customers`, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};
export const accountFeatures = async () => {
  return api
    .get<
      {
        twoFaEnabled: boolean;
        masterPasswordEnabled: boolean;
        notificationsEnabled: boolean;
        emailsEnabled: boolean;
      } & CarnetAccountFeatures
    >(`customers/features`)
    .then((res) => res.data);
};

export const changeFeatureNotification = async (type: 'push' | 'email') => {
  return api.post(`customers/features`, { type }).then((res) => res.data);
};

/** Padrão de multa e juros da conta (boleto e Pix com vencimento). */
export const updateBilletLateCharges = async (body: {
  penaltyPercent?: number;
  penaltyAmount?: number;
  monthlyInterestPercent?: number;
}) => {
  return api.patch<AccountLateCharges>('customers/billet-late-charges', body).then((res) => res.data);
};

export const setMasterPassword = async ({
  masterPassword,
  previousMasterPassword,
}: {
  masterPassword: string;
  previousMasterPassword?: string;
}) => {
  return api
    .post(`customers/master-password`, {
      masterPassword,
      previousMasterPassword,
    })
    .then((res) => res.data);
};

export const getCredentials = async (authorizationId: string) => {
  return api
    .get<{
      clientId: string;
      clientSecret: string;
    }>(`customers/credentials`, {
      headers: {
        'x-security-code': authorizationId,
      },
    })
    .then((res) => res.data);
};

export const createCredentials = () => {
  return api.post(`customers/credentials`).then((res) => res.data);
};

export const getAccounts = ({ page = 0, limit = 10, ...params }: any) => {
  return api.get(`accounts`, { params: { ...params, page, limit } }).then((res) => res.data);
};

export const toggleAccountFavorite = (id: string, isFavorite: boolean) => {
  return api.patch(`accounts/${id}/favorite`, { isFavorite }).then((res) => res.data);
};

// Favorecidos do próprio cliente logado. O backend força a posse (ownership) via /accounts/me:
// exige `pix` OU `account`, mapeia name->accountHolder, document->accountHolderDocument, type->accountType,
// e assume isFavorite=true por padrão.
export interface SaveFavoriteAccountBody {
  pix?: string;
  name?: string;
  document?: string;
  account?: string;
  agency?: string;
  bankIspb?: string;
  type?: string;
  isFavorite?: boolean;
}

export const createAccount = (body: SaveFavoriteAccountBody) => {
  return api.post(`accounts/me`, body).then((res) => res.data);
};

export const updateAccount = (id: string, body: SaveFavoriteAccountBody) => {
  return api.put(`accounts/me/${id}`, body).then((res) => res.data);
};

export const deleteAccount = (id: string) => {
  return api.delete(`accounts/me/${id}`).then((res) => res.data);
};

export const getAntecipationValue = () => {
  return api.get<{ amount: number; amount_total: number }>(`balances-pending/antecipation`).then((res) => res.data);
};

export const getCharge = async (id: string) => {
  return api.get<Charge>(`charges/${id}`).then((res) => res.data);
};

// Baixa o boleto em aberto no banco antes de marcar a cobrança como cancelada.
export const cancelCharge = async (id: string) => {
  return api.post(`charges/${id}/cancel`).then((res) => res.data);
};

// O boleto desenhado por nós (A4, com o lojista no beneficiário). Com
// `responseType: 'blob'` o corpo de ERRO também chega como Blob: leia com
// `blobErrorMessage` — é a mensagem que manda usar o PDF do banco.
export const downloadChargeBilletPdf = (id: string) => {
  return api.get<Blob>(`charges/${id}/billet-pdf`, { responseType: 'blob' });
};

export const findAllCharges = (
  filter: {
    limit: number;
    customerId: string;
    page: number;
    maquinetaId?: string;
    filter: string;
  },
  exportPdf = false,
) => {
  return api
    .post<Orders>(
      'charges/all',
      { ...filter, exportPdf },
      {
        responseType: exportPdf ? 'blob' : 'json',
      },
    )
    .then((res) => res.data);
};

/** Quantidade e valor de cada filtro rápido da listagem de cobranças. */
export const getChargesQuickFilterSummary = (filter: Record<string, any>) => {
  return api
    .post<Record<string, { count: number; amount: number }>>('charges/all/summary', filter)
    .then((res) => res.data);
};

export const getChargeFromPublic = async (id: string) => {
  return api.get<ChargePublic>(`public/charges/${id}`).then((res) => res.data);
};

export const getPublicPagSeguro = async (id: string) => {
  return api.post<{ public_key: string }>(`public/charges/${id}/pagseguro/key`).then((res) => res.data);
};

export const registerBilletForCharge = (id: string, recaptchaToken: string) => {
  return api.put<Charge>(`public/charges/${id}/register-billet`, { recaptchaToken }).then((res) => res.data);
};

export const generatePaymentsForCharge = async (id: string, type: 'pix' | 'billet' | 'instant') => {
  return api.post<Charge>(`charges/${id}/${type}`).then((res) => res.data);
};

export const generateInstantCharge = async (data: {
  amount: number;
  expiration?: number;
  deposit?: boolean;
}) => {
  return api.post<InstantQrCode>(`charges/instant`, data).then((res) => res.data);
};

export const processPaymentMercadoPago = (
  id: string,
  data: ICardPaymentFormData<ICardPaymentBrickPayer> & {
    recaptchaToken: string;
    description?: string;
    email: string;
    document: string;
    name: string;
  },
) => {
  return api.post<Charge>(`public/charges/${id}/mercado-pago`, data).then((res) => res.data);
};

export const payWithCreditCard = async (
  id: string,
  data: FormCardCreditCard & {
    recaptchaToken: string;
    autoRecurrence?: boolean;
    sentinelaSessionId?: string | null;
  },
) => {
  return api.post<Charge>(`public/charges/${id}/credit-card`, data).then((res) => res.data);
};

export const payWithCreditCardRecharge = async (data: RechargeFormCardCreditCard & { recaptchaToken: string }) => {
  return api.post<Charge>(`charges/credit-card`, data).then((res) => res.data);
};

export const payWithCreditCardGetnet = async (
  id: string,
  data: FormCardCreditCard & { recaptchaToken: string; sentinelaSessionId?: string | null },
) => {
  return api.post<Charge>(`public/charges/${id}/getnet/credit-card`, data).then((res) => res.data);
};

export const log3dsFailure = async (
  chargeIdOrNull: string | null,
  data: { requestData: Record<string, any>; responseData?: Record<string, any>; errorMessage?: string },
) => {
  const url = chargeIdOrNull
    ? `public/charges/${chargeIdOrNull}/3ds-failure`
    : `public/3ds-failure`;
  return api.post(url, data).catch(() => {});
};

export const infoAccount = async () => {
  return api.get<CustomerInfo>(`customers/info`).then((res) => res.data);
};

export const findInstallmentsConfig = async () => {
  return api.get<InstallmentsConfig>(`charges/installmentsConfigRecharge`).then((res) => res.data);
};

export const findInstallmentsConfigCustom = async (paymentMethod: string) => {
  return api
    .get<InstallmentsConfig>(`charges/installmentsConfig?paymentMethod=${paymentMethod}`)
    .then((res) => res.data);
};

export const initializePaymentMercadoPago = (id: string) => {
  return api
    .get<{
      publicKey: string;
    }>(`public/charges/${id}/mercado-pago`)
    .then((res) => res.data);
};

export const estornarPix = (chargeId: string) => {
  return api.put(`charges/${chargeId}/pix/refund`).then((res) => res.data);
};
export const request2FaData = () => {
  return api.get<{ otpauth_url: string; base64: string }>('customers/twoFa').then((res) => res.data);
};

export const active2FaData = (token) => {
  return api.post('customers/twoFa', { token }).then((res) => res.data);
};

export const createInsurance = async (data: {
  holderId: string;
  startDate: string;
  modelId: string;
  endDate: string;
}) => {
  return api.post(`insurances`, data).then((res) => res.data);
};

export const createHolder = async (data: CreateHolderForm) => {
  return api.post(`insurances/holders`, data).then((res) => res.data);
};

export const findAllHolders = ({
  limit = 20,
  filter = '',
  page = 0,
}: {
  limit?: number;
  page?: number;
  filter?: string;
}) => {
  return api
    .get<CustomerListResponse>('insurances/holders', {
      params: {
        limit,
        page,
        filter,
      },
    })
    .then((res) => res.data);
};

export const findAllInsurancesModels = ({
  limit = 20,
  filter = '',
  page = 0,
}: {
  limit?: number;
  page?: number;
  filter?: string;
}) => {
  return api
    .get<CustomerListResponse>('insurances/models', {
      params: {
        limit,
        page,
        filter,
      },
    })
    .then((res) => res.data);
};

export const findAllInsurances = ({
  limit,
  page,
  companyId,
  filter,
}: {
  limit: number;
  companyId: string;
  page: number;
  filter: string;
}) => {
  return api
    .get<Orders>('insurances', {
      params: { filter, limit, page, companyId },
    })
    .then((res) => res.data);
};
export const cancelInsurance = async ({ id }: { id: string }) => {
  return api.delete(`insurances/${id}`).then((res) => res.data);
};

export const findCards = async ({ limit = 10, page = 0 }: { limit: number; page: number } | undefined) => {
  return api.post<CardList>(`cards/all`, { limit, page }).then((res) => res.data);
};
export const findAllMovementsCard = ({
  limit = 20,
  filter = '',
  page = 0,
  companyId,
  customerId,
  cardId,
}: {
  limit?: number;
  page?: number;
  filter?: string;
  companyId?: string;
  customerId?: string;
  cardId: string;
}) => {
  return api
    .get<any>(`cards/${cardId}/movements`, {
      params: {
        limit: +limit,
        page: +page,
        companyId,
        customerId,
        filter,
      },
    })
    .then((res) => res.data);
};

export interface CardBrandOption {
  label: string;
  value: string;
}
export const findCardBrands = async () => {
  return api.get<CardBrandOption[]>(`cards/brands`).then((res) => res.data);
};

export const startOnboarding = async (data: {
  captchaToken?: string;
  turnstileToken?: string;
  type: 'PF' | 'PJ';
}) => {
  return api
    .post<{
      id: string;
    }>('onboarding-customer', data)
    .then((res) => res.data);
};

export type NextOnboarding = {
  step: 'credentials' | 'company' | 'personal' | 'address' | 'documents' | 'selfie' | 'complete';
  [key: string]: any;
};

export const nextOnboarding = async (id: string, data: NextOnboarding) => {
  return api
    .put<{
      id: string;
    }>(`onboarding-customer/${id}`, data)
    .then((res) => res.data);
};

export const findOnboarding = async (id: string) => {
  return api.get<Onboarding>(`onboarding-customer/${id}`).then((res) => res.data);
};

export const findAllRecurrencyLinks = async (filter: { limit: number; page: number; filter: string }) => {
  return api.post<RecurrencyLink>(`recurrency-links/all`, filter).then((res) => res.data);
};

export const createRecurrencyLink = async (data: {
  title: string;
  description: string;
  image: string;
  itens: { planId: string }[];
}) => {
  return api.post<RecurrencyLink>(`recurrency-links`, data).then((res) => res.data);
};

export const updateRecurrencyLink = async (id: string, data: { title: string; description: string }) => {
  return api.put<RecurrencyLink>(`recurrency-links/${id}`, data).then((res) => res.data);
};

export const findRecurrencyLink = async (id: string) => {
  return api.get<RecurrencyLinkItemGet>(`recurrency-links/${id}`).then((res) => res.data);
};

export const findRecurrencyLinkPublic = async (id: string) => {
  return api.get<RecurrencyLinkItemGet>(`recurrency-links/public/${id}`).then((res) => res.data);
};

export const addPlanToRecurrencyLink = async (id: string, data: { planId: string }) => {
  return api.post<RecurrencyLink>(`recurrency-links/${id}/item`, data).then((res) => res.data);
};

export const removePlanFromRecurrencyLink = async (id: string, data: { planId: string }) => {
  return api.delete<RecurrencyLink>(`recurrency-links/${id}/item/${data.planId}`).then((res) => res.data);
};

export const updateRecurrency = (id: string, data: any) => {
  return api.put(`/recurrences/${id}`, data);
};

export const generateRecurrencyLink = async (
  id: string,
  data: { name: string; email: string; document: string; reffererId?: string; phone: string; coupon?: string },
) => {
  return api
    .post<{
      link: string;
    }>(`recurrency-links/${id}/generate`, data)
    .then((res) => res.data);
};

export const findTerminalsApp = (filters: {
  limit: number;
  page: number;
  filter: string;
  orderBy: { id: string; desc: boolean }[];
}) => {
  return api
    .get<{ total: number; registers: any[] }>('terminals', {
      params: filters,
    })
    .then((res) => res.data);
};

export const deleteTerminal = async (id: string) => {
  return api.delete(`maquinetas/${id}`).then((res) => res.data);
};

export const createTerminal = async (data: { name: string; email: string; document: string; phone?: string }) => {
  return api.post(`terminals`, data).then((res) => res.data);
};

export const editTerminal = async (
  id: string,
  data: {
    name: string;
    email: string;
    document: string;
    phone?: string;
  },
) => {
  return api.put(`terminals/${id}`, data).then((res) => res.data);
};

export const findSharedRecurrencyLink = async (id: string) => {
  return api
    .get<{
      url: string;
    }>(`recurrency-links/shared/${id}`)
    .then((res) => res.data);
};

export const generateRecurrenceReport = async (id: string) => {
  return api
    .get<{
      url: string;
    }>(`recurrency-links/${id}/report`, {
      responseType: 'blob',
    })
    .then((res) => res.data);
};

export const findDispute = async (id: string) => {
  return api.get<Dispute>(`disputes/public/${id}`).then((res) => res.data);
};

export const sendMessageDispute = async (id: string, data: { message: string }) => {
  return api.post<Dispute>(`disputes/public/${id}/message`, data).then((res) => res.data);
};

export const sendFileDispute = async (id: string, data: { file?: File }) => {
  const formData = new FormData();
  if (data.file) {
    formData.append('file', data.file);
  }
  return api
    .post<Dispute>(`disputes/public/${id}/file`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then((res) => res.data);
};

export const saveAccountPixDefault = async (data: { pixKey: string; document: string; name: string }) => {
  return api.post(`customers/pix-default`, data).then((res) => res.data);
};

export const getAccountPixDefault = async () => {
  return api.get(`customers/pix-default`).then((res) => res.data);
};

export const validateCouponApi = async (planId: string, coupon: string) => {
  return api
    .post<{
      coupon: string;
      discount: number;
    }>(`recurrency-links/${planId}/coupon`, { coupon })
    .then((res) => res.data);
};

// Account Access Management
export interface CustomerSearchResult {
  id: string;
  name: string;
  email: string;
  accountNumber: string;
  document_number: string;
}

export interface AccountAccess {
  id: string;
  customerAccessId: string;
  validTo: string;
  isMaster: boolean;
  customerAccess: {
    id: string;
    name: string;
    email: string;
    accountNumber: string;
  };
}

export const searchCustomerByAccount = async (accountNumber: string) => {
  return api.get<CustomerSearchResult>(`customers/search/account/${accountNumber}`).then((res) => res.data);
};

export const getMyAccountAccess = async (customerId: string) => {
  return api.get<AccountAccess[]>(`customers/${customerId}/access`).then((res) => res.data);
};

export const addMyAccountAccess = async (
  customerId: string,
  data: { customerAccessId: string; validTo: string; isMaster: boolean },
) => {
  return api.post<AccountAccess>(`customers/${customerId}/access`, data).then((res) => res.data);
};

export const removeMyAccountAccess = async (customerId: string, accessId: string) => {
  return api.delete(`customers/${customerId}/access/${accessId}`).then((res) => res.data);
};

export const getScheduledWithdrawTimes = async (): Promise<string[]> => {
  const { data } = await api.get('/platform-settings/scheduled-withdraw-times')
  return data
}

// Charge Batches
export type ChargeBatchStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'PARTIALLY_COMPLETED' | 'FAILED' | 'CANCELED';
export type ChargeBatchItemStatus = 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'SKIPPED_DUPLICATE' | 'CANCELED';

export interface ChargeBatch {
  id: string;
  status: ChargeBatchStatus;
  totalItems: number;
  processedItems: number;
  succeededItems: number;
  failedItems: number;
  callbackUrl?: string | null;
  createdAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
}

export interface ChargeBatchItem {
  id: string;
  index: number;
  externalId: string;
  status: ChargeBatchItemStatus;
  chargeId?: string | null;
  error?: { code?: string | number; message?: string } | null;
  attempts: number;
  processedAt?: string | null;
}

// Flags de feature da empresa do usuário logado (mapa key->boolean), para gatear menu/UI.
export const getMyFeatureFlags = () => {
  return api.get<Record<string, boolean>>('feature-flags/my').then((res) => res.data);
};

// Paginação no padrão do app: page 0-based + limit; o backend já devolve { registers, pages, total }.
export const findChargeBatches = (filter: {
  limit: number;
  page: number;
  filter?: string;
  status?: string;
}) => {
  return api
    .get<{ registers: ChargeBatch[]; total: number; pages: number }>('charges/batch', {
      params: {
        status: filter.status || undefined,
        page: filter.page,
        limit: filter.limit,
      },
    })
    .then((res) => res.data);
};

export const getChargeBatch = (id: string) => {
  return api.get<ChargeBatch>(`charges/batch/${id}`).then((res) => res.data);
};

export const findChargeBatchItems = (
  id: string,
  filter: { limit: number; page: number; status?: string },
) => {
  return api
    .get<{ registers: ChargeBatchItem[]; total: number; pages: number }>(
      `charges/batch/${id}/items`,
      {
        params: {
          status: filter.status || undefined,
          page: filter.page,
          limit: filter.limit,
        },
      },
    )
    .then((res) => res.data);
};

export const retryChargeBatch = (id: string) => {
  return api.post<{ batchId: string; requeued: number }>(`charges/batch/${id}/retry`).then((res) => res.data);
};

// ===== CARNÊ DE BOLETOS =====
// O carnê é um lote de cobranças (kind BILLET_CARNET). Lista, status e parcelas
// saem das rotas de lote; emissão, PDF e retry têm rota própria em
// /charges/billet-carnet, que não passa pela flag charges_batch_enabled.

/**
 * A Idempotency-Key é de quem chama, não gerada aqui: a tela mantém a MESMA
 * chave entre tentativas, senão um retry de rede vira um segundo carnê — e cada
 * parcela é um registro bancário tarifado.
 */
export const createCarnet = (body: CreateCarnetRequest, idempotencyKey: string) => {
  return api
    .post<{ batchId: string; status: string; totalItems: number }>('charges/billet-carnet', body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    })
    .then((res) => res.data);
};

export const findCarnets = (filter: { limit: number; page: number; search?: string }) => {
  return api
    .get<{ registers: Carnet[]; total: number; pages: number }>('charges/batch', {
      params: {
        kind: 'BILLET_CARNET',
        page: filter.page,
        limit: filter.limit,
        ...(filter.search && { search: filter.search }),
      },
    })
    .then((res) => res.data);
};

export const getCarnet = (id: string) => {
  return api.get<Carnet>(`charges/batch/${id}`).then((res) => res.data);
};

// Um carnê tem no máximo 24 parcelas: cabe numa página só.
export const findCarnetItems = (id: string) => {
  return api
    .get<{ registers: CarnetItem[]; total: number; pages: number }>(`charges/batch/${id}/items`, {
      params: { page: 0, limit: 24 },
    })
    .then((res) => res.data);
};

// Rota do carnê, não a do lote (`charges/batch/:id/retry`): aquela passa pelo
// guard da flag charges_batch_enabled, default-deny, e devolveria 403.
export const retryCarnet = (id: string) => {
  return api.post<{ batchId: string; requeued: number }>(`charges/billet-carnet/${id}/retry`).then((res) => res.data);
};

// `responseType: 'blob'` faz o corpo de ERRO também chegar como Blob — quem
// chama lê com `blobErrorMessage`, que é a mensagem que nomeia a parcela.
export const downloadCarnetPdf = (id: string) => {
  return api.get<Blob>(`charges/billet-carnet/${id}/pdf`, { responseType: 'blob' });
};

// ===== WITHDRAWAL BATCHES =====
export type WithdrawalBatchStatus =
  | 'PENDING'
  | 'PENDING_APPROVAL'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'PARTIALLY_COMPLETED'
  | 'FAILED'
  | 'CANCELED'

export type WithdrawalBatchItemStatus = 'PENDING' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED' | 'SCHEDULED'

export interface ScheduledWithdrawalRef {
  id: string
  scheduledFor: string
  status: 'SCHEDULED' | 'PROCESSING' | 'EXECUTED' | 'FAILED' | 'CANCELED'
  executedWithdrawalId?: string | null
  executedAt?: string | null
  lastError?: string | null
}

export interface WithdrawalBatch {
  id: string
  status: WithdrawalBatchStatus
  fileName?: string | null
  totalItems: number
  totalAmount: string
  processedItems: number
  succeededItems: number
  failedItems: number
  scheduledItems: number
  createdAt: string
  startedAt?: string | null
  finishedAt?: string | null
}

export interface WithdrawalBatchItem {
  id: string
  index: number
  externalId: string
  name?: string | null
  document?: string | null
  pixKey: string
  pixKeyType?: string | null
  description?: string | null
  amount: string
  status: WithdrawalBatchItemStatus
  withdrawalId?: string | null
  scheduledFor?: string | null
  scheduledWithdrawal?: ScheduledWithdrawalRef | null
  error?: { code?: string | number; message?: string } | null
}

export interface BatchItemPayload {
  externalId: string
  pixKey: string
  amount: number
  name?: string
  document?: string
  pixKeyType?: string
  description?: string
  scheduledFor?: string | null
}

export const findWithdrawalBatches = (filter: { limit: number; page: number; status?: string }) => {
  return api
    .get<{ registers: WithdrawalBatch[]; total: number; pages: number }>('withdrawals/batch', {
      params: { status: filter.status || undefined, page: filter.page, limit: filter.limit },
    })
    .then((res) => res.data)
}

export const getWithdrawalBatch = (id: string) => {
  return api.get<WithdrawalBatch>(`withdrawals/batch/${id}`).then((res) => res.data)
}

export const findWithdrawalBatchItems = (id: string, filter: { limit: number; page: number; status?: string }) => {
  return api
    .get<{ registers: WithdrawalBatchItem[]; total: number; pages: number }>(`withdrawals/batch/${id}/items`, {
      params: { status: filter.status || undefined, page: filter.page, limit: filter.limit },
    })
    .then((res) => res.data)
}

export const createWithdrawalBatch = (data: { items: BatchItemPayload[]; fileName?: string }) => {
  return api
    .post<{ batchId: string; status: WithdrawalBatchStatus; totalItems: number }>('withdrawals/batch', data)
    .then((res) => res.data)
}

export const addWithdrawalBatchItem = (id: string, data: BatchItemPayload) => {
  return api.post<WithdrawalBatchItem>(`withdrawals/batch/${id}/items`, data).then((res) => res.data)
}

export const updateWithdrawalBatchItem = (id: string, itemId: string, data: Partial<BatchItemPayload>) => {
  return api.put<WithdrawalBatchItem>(`withdrawals/batch/${id}/items/${itemId}`, data).then((res) => res.data)
}

export const deleteWithdrawalBatchItem = (id: string, itemId: string) => {
  return api.delete(`withdrawals/batch/${id}/items/${itemId}`).then((res) => res.data)
}

export const confirmWithdrawalBatch = (id: string, authorizationId: string) => {
  return api
    .post<{ batchId: string; status: WithdrawalBatchStatus }>(`withdrawals/batch/${id}/confirm`, {}, {
      headers: { 'x-security-code': authorizationId },
    })
    .then((res) => res.data)
}

export const approveWithdrawalBatch = (id: string) => {
  return api.post(`withdrawals/batch/${id}/approve`).then((res) => res.data)
}

export const rejectWithdrawalBatch = (id: string) => {
  return api.post(`withdrawals/batch/${id}/reject`).then((res) => res.data)
}

export const retryWithdrawalBatch = (id: string) => {
  return api.post<{ batchId: string; requeued: number }>(`withdrawals/batch/${id}/retry`).then((res) => res.data)
}

export const deleteWithdrawalBatch = (id: string) => {
  return api.delete(`withdrawals/batch/${id}`).then((res) => res.data)
}

export const createFeedback = async (data: { type: string; message: string; phone?: string }) => {
  return api.post('feedbacks', data).then((res) => res.data);
};

export interface ProductGroupSplit {
  id?: string;
  receiverCustomerId: string;
  percentage: number;
}

export interface ProductGroup {
  id: string;
  name: string;
  applyCashSplit: boolean;
  active: boolean;
  splits: ProductGroupSplit[];
}

export interface ResolvedReceiver {
  customerId: string;
  name: string;
  accountNumber: string;
  email: string;
}

export const resolveCatalogReceiver = async (params: { email?: string; account?: string }) => {
  return api.get<ResolvedReceiver>('catalog/receivers/resolve', { params }).then((res) => res.data);
};

export const findProductGroups = async () => {
  return api.get<ProductGroup[]>('catalog/groups').then((res) => res.data);
};

export const getProductGroup = async (id: string) => {
  return api.get<ProductGroup>(`catalog/groups/${id}`).then((res) => res.data);
};

export const createProductGroup = async (data: {
  name: string;
  applyCashSplit: boolean;
  splits: { receiverCustomerId: string; percentage: number }[];
}) => {
  return api.post('catalog/groups', data).then((res) => res.data);
};

export const updateProductGroup = async (
  id: string,
  data: { name?: string; applyCashSplit?: boolean; splits?: { receiverCustomerId: string; percentage: number }[] },
) => {
  return api.patch(`catalog/groups/${id}`, data).then((res) => res.data);
};

export const deleteProductGroup = async (id: string) => {
  return api.delete(`catalog/groups/${id}`).then((res) => res.data);
};

export interface ProductSplit {
  id?: string;
  receiverCustomerId: string;
  percentage: number;
}

export interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  productGroupId: string;
  imageUrl: string | null;
  imageLowUrl: string | null;
  splits: ProductSplit[];
}

export const findProducts = async (groupId?: string) =>
  api.get<Product[]>('catalog/products', { params: groupId ? { groupId } : {} }).then((res) => res.data);

export const getProduct = async (id: string) => api.get<Product>(`catalog/products/${id}`).then((res) => res.data);

export const createProduct = async (data: {
  name: string;
  productGroupId: string;
  price: number;
  stock?: number;
  splits?: { receiverCustomerId: string; percentage: number }[];
}) => api.post<Product>('catalog/products', data).then((res) => res.data);

export const updateProduct = async (
  id: string,
  data: {
    name?: string;
    productGroupId?: string;
    price?: number;
    stock?: number;
    splits?: { receiverCustomerId: string; percentage: number }[];
  },
) => api.patch<Product>(`catalog/products/${id}`, data).then((res) => res.data);

export const deleteProduct = async (id: string) => api.delete(`catalog/products/${id}`).then((res) => res.data);

export const setProductImage = async (id: string, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api
    .post<Product>(`catalog/products/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    .then((res) => res.data);
};

export const deleteProductImage = async (id: string) =>
  api.delete(`catalog/products/${id}/image`).then((res) => res.data);

// Extrato Consolidado (lojista): vendas do próprio customer agrupadas por dia/mês
// e por forma de pagamento. Backend força customerId/companyId a partir do JWT —
// nunca envie esses campos aqui.
export const getConsolidatedReport = (filter: any) => {
  return api.get<ConsolidatedReport>('reports/consolidated/customer', { params: filter }).then((res) => res.data);
};

export default api;

// ---------------------------------------------------------------------------
// Cobranças programadas (visão do pagador) e pagamento agendado
// ---------------------------------------------------------------------------

export interface ScheduledChargeInstallmentView {
  id: string;
  scheduledChargeId: string;
  number: number;
  dueDate: string;
  originalAmount: string;
  settledAmount: string;
  penaltyApplied: string;
  lateInterestAccrued: string;
  status: string;
  settledAt: string | null;
  /** Calculados no servidor em Decimal. */
  outstanding: string;
  daysOverdue: number;
  /** Rótulo pronto: descrição do contrato ou o tipo. */
  typeLabel: string;
  scheduledCharge?: {
    id: string;
    type: string;
    description: string | null;
    Receiver?: { id: string; name: string };
  };
}

export const getMyScheduledCharges = (params: {
  status?: string;
  dueFrom?: string;
  dueTo?: string;
  onlyOverdue?: boolean;
  page?: number;
  limit?: number;
}) =>
  api
    .get<{ data: ScheduledChargeInstallmentView[]; total: number }>('scheduled-charges/me', {
      params,
    })
    .then((res) => res.data);

export const getMyScheduledChargeDebt = () =>
  api
    .get<{
      total: string;
      openInstallments: string;
      negativeBalance: string;
      installments: number;
      oldestDueDate: string | null;
    }>(
      'scheduled-charges/me/debt',
    )
    .then((res) => res.data);

export interface ScheduledWithdrawalView {
  id: string;
  scheduledFor: string;
  amount: string;
  type: string;
  description: string | null;
  status: 'SCHEDULED' | 'PROCESSING' | 'EXECUTED' | 'FAILED' | 'CANCELED';
  attempts: number;
  lastError: string | null;
  executedWithdrawalId: string | null;
}

export const scheduleWithdrawal = (authorizationId: string, data: any) =>
  api
    .post<ScheduledWithdrawalView>('withdrawals/schedule', data, {
      headers: { 'x-security-code': authorizationId },
    })
    .then((res) => res.data);

export const getScheduledWithdrawals = (status?: string) =>
  api
    .get<ScheduledWithdrawalView[]>('withdrawals/schedule', { params: { status } })
    .then((res) => res.data);

export const cancelScheduledWithdrawal = (id: string) =>
  api.delete(`withdrawals/schedule/${id}`).then((res) => res.data);
